import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { uploadEmailCampaignImage } from '../../../utils/emailApi';
import { gymModalLabel, gymSocialMutedText } from '../gymPageTheme';
import './GymEmailCampaignEditor.css';

const BODY_VARIABLES = ['{{nome}}', '{{palestra}}'];

/**
 * Editor HTML campagne email (Quill) con upload immagini su server.
 */
export default function GymEmailCampaignEditor({
  value,
  onChange,
  disabled,
  onInsertVariable,
}) {
  const { t } = useTranslation();
  const quillRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const insertUploadedImage = useCallback(async (file) => {
    if (!file || disabled) {
      return;
    }
    setUploadingImage(true);
    try {
      const data = await uploadEmailCampaignImage(file);
      const editor = quillRef.current?.getEditor?.();
      if (!editor || !data.url) {
        throw new Error('Editor non pronto');
      }
      const range = editor.getSelection(true);
      const index = range ? range.index : editor.getLength();
      editor.insertEmbed(index, 'image', data.url, 'user');
      editor.setSelection(index + 1);
      toast.success(t('gym.email.campaigns.imageUploaded'));
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || t('gym.email.campaigns.imageUploadError'));
    } finally {
      setUploadingImage(false);
    }
  }, [disabled, t]);

  const imageHandler = useCallback(() => {
    if (disabled || uploadingImage) {
      return;
    }
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/jpeg,image/png,image/webp');
    input.click();
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        insertUploadedImage(file);
      }
    };
  }, [disabled, uploadingImage, insertUploadedImage]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) {
      return undefined;
    }
    const onPaste = (event) => {
      const items = event.clipboardData?.items;
      if (!items) {
        return;
      }
      const imageItem = Array.from(items).find((item) => item.type && item.type.startsWith('image/'));
      if (!imageItem) {
        return;
      }
      const file = imageItem.getAsFile();
      if (!file) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      insertUploadedImage(file);
    };
    editor.root.addEventListener('paste', onPaste);
    return () => editor.root.removeEventListener('paste', onPaste);
  }, [insertUploadedImage]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          ['bold', 'italic', 'underline'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          ['link', 'image'],
          ['clean'],
        ],
        handlers: {
          image: imageHandler,
        },
      },
      clipboard: { matchVisual: false },
    }),
    [imageHandler]
  );

  const formats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'image'];

  const handleVariableClick = (variable) => {
    if (onInsertVariable) {
      onInsertVariable(variable);
      return;
    }
    const editor = quillRef.current?.getEditor?.();
    if (!editor || disabled) {
      onChange(`${value || ''}${variable}`);
      return;
    }
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    editor.insertText(index, variable, 'user');
    editor.setSelection(index + variable.length);
  };

  return (
    <div className="gym-email-campaign-editor flex h-full flex-col">
      <span className={gymModalLabel}>{t('gym.email.campaigns.body')}</span>
      <div className="mt-2 flex min-h-8 flex-wrap items-center gap-2">
        <span className={`text-xs ${gymSocialMutedText}`}>{t('gym.email.campaigns.insertVariable')}</span>
        {BODY_VARIABLES.map((variable) => (
          <button
            key={variable}
            type="button"
            disabled={disabled}
            onClick={() => handleVariableClick(variable)}
            className="gym-email-editor-var-chip"
            title={t('gym.email.campaigns.insertVariableTitle', { variable })}
          >
            {variable}
          </button>
        ))}
      </div>
      <div className={`gym-email-quill-wrap mt-2 flex min-h-0 flex-1 flex-col ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          useSemanticHTML={false}
          placeholder={t('gym.email.campaigns.bodyPlaceholder')}
          readOnly={disabled}
        />
        {uploadingImage && (
          <div className="gym-email-quill-uploading" aria-hidden>
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
